import { Typography } from '@packages/shared';
import type { ReactNode } from 'react';
import { useLayerTracking } from '@packages/modules/src/Tracking/hooks/useLayerTracking/useLayerTracking';
import { useIntl } from 'react-intl';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { TaxHintFragmentFragmentDoc } from '@packages/gql/generated/shopping/TaxHintFragmentFragmentDoc';
import { ServiceLayer } from '@packages/cms-components/src/modules/ServiceLayer/ServiceLayer';
import { TextWithDialog } from '../TextWithDialog';
import { taxHintMessages } from './messages';
import { getTaxHintMessage } from './getTaxHintMessage';

/* GraphQL */ `
  fragment TaxHintFragment on Delivery {
    deliverySize
  }
`;

export type TaxHintProps = {
  /**
   * The used delivery type for a given product. Each delivery type will show its own delivery info message.
   * */
  data: FragmentType<typeof TaxHintFragmentFragmentDoc>;
};

/**
 * <p>
 * The <code>TaxHint</code> component displays a delivery info message.
 * </p>
 * */
export const TaxHint = ({ data }: TaxHintProps) => {
  const { deliverySize } = getFragmentData(TaxHintFragmentFragmentDoc, data);
  const { formatMessage } = useIntl();
  const { trackViewLayer } = useLayerTracking();
  const {
    product: {
      taxHint: { hideAdditionalDeliveryInfo, modalContent },
    },
  } = useConfig();

  const dialogContent =
    deliverySize in modalContent ? (
      <ServiceLayer serviceLayerId={modalContent[deliverySize].link} />
    ) : (
      <>{formatMessage(taxHintMessages.dialogContent)}</>
    );

  const title = formatMessage(getTaxHintMessage('TITLE', deliverySize));

  const handleDialogOpen = () => {
    trackViewLayer(`${title} (Liefertyp: ${deliverySize})`);
  };

  const messageValue = {
    link: (msg: ReactNode[]) =>
      !hideAdditionalDeliveryInfo && (
        <TextWithDialog
          message={msg}
          dialogTitle={title}
          dialogContent={dialogContent}
          onOpenDialogHandler={() => handleDialogOpen()}
          variant="body3"
          sx={{ color: 'text.secondary' }}
          displayTrackingProps={{ category: 'dv-service-layer-tax-hint' }}
          key={msg.toString()}
        />
      ),
  };

  return (
    <Typography variant="body3" component="p" sx={{ color: 'text.secondary' }}>
      {formatMessage(getTaxHintMessage('TEXT', deliverySize), messageValue)}
    </Typography>
  );
};
